import styles from './RentalListing.module.css';

import type { FC } from 'react';
import type { Currency } from 'src/types/listing';
import type { ApiUserAccount } from 'src/types/userAuth';
import type { ApiRentalBookingQuote } from 'src/types/rental';

import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { X } from '@phosphor-icons/react';
import { Pagination } from 'swiper/modules';
import React, { useMemo, useState } from 'react';
import { SwiperSlide, Swiper as SwiperContainer } from 'swiper/react';
import { useParams, useNavigate, useSearchParams } from 'react-router';

import { useEscrowOffer } from 'src/hooks/useEscrowOffer';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { formatDay } from 'src/utils/date';
import { formatAmount } from 'src/utils/money';
import { rentalListingHead } from 'src/utils/pageHead';
import { formatImageUrl } from 'src/utils/format-image';
import { formatBaseUnits } from 'src/utils/rentalInvoice';
import { carAttributeRows } from 'src/utils/carAttributes';
import { rentalCarTermRows } from 'src/utils/rentalCarTerms';
import { noPhotoAlt, carPhotoAlt } from 'src/utils/imageAlt';
import { resolvePurchaseGate } from 'src/utils/purchaseGate';
import { describePricingBands } from 'src/utils/rentalPricing';
import { rentalBookingDay } from 'src/utils/rentalBookingDates';
import { bookingDatesFromSearch } from 'src/utils/bookingDatesFromSearch';
import { quoteDecimals, describeEscrowOffer } from 'src/utils/escrowOffer';
import { hasBookingConflict, openEndedOccupancyNote } from 'src/utils/rentalOccupancy';

import currencyImage from 'src/assets/images/currency.png';
import {
  useBuyerChains,
  useRentalListing,
  useCreateRentalDeal,
  useRentalBookingQuote,
} from 'src/queries';

import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { DocumentHead } from 'src/components/DocumentHead';
import { Typography } from 'src/components/themed/Typography';
import { RequestFailureState } from 'src/components/EmptyState';
import { ActionButton } from 'src/components/themed/ActionButton';
import { SafeHtml, listingDescriptionText } from 'src/components/SafeHtml';

import { RentalDealMode } from '../../types/rental';
import { BookingCalendar } from './BookingCalendar';

interface RentalListingProps {
  /** An account session is held on this device. */
  isLoggedIn: boolean;
  /** The account behind it (`/user/auth/me`), once the api has answered. */
  account?: ApiUserAccount;
  /** The account holds a linked wallet; `undefined` while it is not known yet (`useSession`). */
  hasLinkedWallet?: boolean;
  isTMA?: boolean;
  /** A session held on this device is still being checked: the booking control waits (see `Listing`). */
  sessionPending?: boolean;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Post-return inspection window during which the car stays occupied. Mirror of
 * api INSPECTION_TAIL_DAYS / INSPECTION_LOCK_PERIOD (7 days) in RentalDeal.sol —
 * the backend rejects a booking whose [start, end + tail) overlaps another
 * booking's occupied window, so the form validates the same rule up front.
 */
const INSPECTION_TAIL_DAYS = 7;

/** Rental listing detail + booking form. Pickup mode is fixed to onsite in this slice. */
export const RentalListing: FC<RentalListingProps> = ({ isLoggedIn, hasLinkedWallet, isTMA, sessionPending }) => {
  const { id: listingId } = useParams();
  const navigate = useNavigate();
  const query = useRentalListing(listingId!);
  const { data: listing, refetch } = query;
  const createRentalDeal = useCreateRentalDeal();
  // Same as the sale listing: a car that is gone says so and offers the way back (BUG-03).
  const state = useQueryScreenState(query);

  const [searchParams] = useSearchParams();
  // Dates in the address — "Book these dates" of the assistant's panel (api#381) — start the form
  // filled in. Read once, when the screen mounts: a later change of the address is a navigation.
  // Whether a day is past is judged by the clock of whoever renders; the form itself is rendered
  // only to a signed-in person, in the browser, so the server's answer never reaches the markup.
  // The calendar below stays the judge of conflicts.
  const [arrived] = useState(() => bookingDatesFromSearch(searchParams, dayjs().format('YYYY-MM-DD')));
  const [startDate, setStartDate] = useState(arrived.startDate);
  const [endDate, setEndDate] = useState(arrived.endDate);
  const [error, setError] = useState('');

  const days = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const diff = dayjs(endDate).startOf('day').diff(dayjs(startDate).startOf('day'));
    return Math.round(diff / MS_PER_DAY);
  }, [startDate, endDate]);

  const validationError = useMemo(() => {
    if (!listing || !startDate || !endDate) return '';
    if (days <= 0) return 'End date must be after the start date.';
    if (days < listing.minRentalDays) return `Minimum rental is ${listing.minRentalDays} day(s).`;
    if (days > listing.maxRentalDays) return `Maximum rental is ${listing.maxRentalDays} day(s).`;

    // Mirror the backend overlap guard: the requested window extended by the
    // inspection tail must not overlap any occupied window of this listing. An
    // open-ended hold (`endDate: null`) conflicts with any window ending after
    // its start.
    const requestedStart = dayjs(startDate).startOf('day');
    const requestedEnd = dayjs(endDate).startOf('day').add(INSPECTION_TAIL_DAYS, 'day');
    const conflict = hasBookingConflict(requestedStart, requestedEnd, listing.occupiedRanges ?? []);
    if (conflict) {
      return `These dates conflict with an existing booking — the car also needs a ${INSPECTION_TAIL_DAYS}-day inspection window after each return. Pick different dates.`;
    }

    return '';
  }, [listing, startDate, endDate, days]);

  // The price comes from the server, not from arithmetic here (spec §2): it knows which duration
  // band the booking falls into, and it computes the 9% platform fee with the same integer math
  // the escrow contract uses — so "Total now" is exactly what the wallet is later asked for.
  const { data: quote } = useRentalBookingQuote(
    listingId!,
    validationError ? '' : startDate,
    validationError ? '' : endDate
  );

  const bands = useMemo(() => describePricingBands(listing), [listing]);

  const handleClose = () => {
    navigate('/rent', { replace: true });
  };

  const fromPath = `/rentals/listing/${listingId}`;

  // Anonymous → register first (§5 gate).
  const handleRegister = () => {
    navigate(`/register?plan=personal&from=${encodeURIComponent(fromPath)}`);
  };

  // An account with no wallet links one in Profile (Linked wallets), since booking still needs a
  // wallet to pay from (decision #5) — and comes back here through the tab bar.
  const handleLinkWallet = () => {
    navigate('/profile');
  };

  // account-first: an account is the first barrier (§5 gate); booking resolves the account
  // session to its primary wallet (@Protected({ forWallet, forAccount })), so an account with
  // none is sent to link one.
  const purchaseGate = resolvePurchaseGate({ isLoggedIn, hasLinkedWallet, isTMA: !!isTMA });

  // Past the gate, Book is offered only where the registry says this renter can pay the deposit
  // (app#176): the chain that will host the rental must be one the account has a wallet in.
  const escrowOffer = useEscrowOffer({
    vertical: 'rental',
    listingCurrency: listing?.currency,
    purchaseGate,
  });

  // The quote's money lines are base units of the token this rental will settle in — the one the
  // quote names (`cryptoCurrency`: USDC for a dollar car, EURC for a euro one). Its decimals come
  // from the same registry the offer above reads (one request, shared), through the network that
  // hosts rentals, and must name the same token as the quote; the build knows no token on its own.
  // Asked for only past the gate, like the offer: an anonymous visitor's page is rendered on the
  // server and makes no such request.
  const { data: buyerChains } = useBuyerChains(purchaseGate === 'buy');

  const handleBook = async () => {
    if (!listing || validationError) {
      setError(validationError || 'Please pick your rental dates.');
      return;
    }
    setError('');
    try {
      const deal = await createRentalDeal.mutateAsync({
        listingId: listing.id,
        // The same calendar day the quote above was asked about — see rentalBookingDay.
        // A moment in time here priced one interval and booked another.
        startDate: rentalBookingDay(startDate),
        endDate: rentalBookingDay(endDate),
        mode: RentalDealMode.ONSITE,
      });
      navigate(`/rental/${deal.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the booking. Please try again.');
    }
  };

  return (
    <div className={classNames('container', styles.container)}>
      {listing ? (
        <DocumentHead {...rentalListingHead(listing, listingDescriptionText(listing.description))} />
      ) : (
        <DocumentHead title="Rent" />
      )}
      {state === 'loading' || createRentalDeal.isPending ? (
        <Loader />
      ) : state !== 'ready' || !listing ? (
        <Stack width="100%" p={4} pb={20} maxWidth="750px">
          <RequestFailureState
            titleAs="h1"
            kind={state === 'ready' ? 'failed' : state}
            subject="This car"
            onBack={handleClose}
            backLabel="Back to rentals"
            onRetry={() => refetch()}
          />
        </Stack>
      ) : (
        <Stack width="100%" gap={4} p={4} pb={20} maxWidth="750px">
          <Stack
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            pl={4}
          >
            <Typography as="h1" fontWeight="600" variant="title3" color="white">
              {listing.make.name} {listing.model.name}
            </Typography>
            <ActionButton onClick={handleClose} label="Back to rentals">
              <X size={24} color="white" />
            </ActionButton>
          </Stack>

          {!isEmpty(listing.images) ? (
            <div className={styles.swiper}>
              <SwiperContainer
                modules={[Pagination]}
                pagination={{ clickable: false }}
                allowTouchMove
                height={320}
              >
                {listing.images.map((i, index, all) => (
                  <SwiperSlide key={i}>
                    <img
                      src={formatImageUrl(i)}
                      alt={carPhotoAlt(listing, index, all.length)}
                      className={styles.image}
                    />
                  </SwiperSlide>
                ))}
              </SwiperContainer>
            </div>
          ) : (
            <img
              src={currencyImage}
              alt={noPhotoAlt(listing)}
              className={classNames(styles.image, styles.defaultImage)}
            />
          )}

          <Stack gap={1} ph={4}>
            {bands.map((band) => (
              <Row
                key={band.label}
                label={band.label}
                value={`${formatAmount(band.dailyPrice)} ${listing.currency} / day`}
              />
            ))}
            <Row
              label="Deposit"
              value={`${formatAmount(listing.depositAmount)} ${listing.currency}`}
            />
            <Row label="Class" value={listing.vehicleClass} />
            {carAttributeRows(listing).map(({ label, value }) => (
              <Row key={label} label={label} value={value} />
            ))}
            {rentalCarTermRows(listing).map(({ label, value }) => (
              <Row key={label} label={label} value={value} />
            ))}
            <Row
              label="Rental window"
              value={`${listing.minRentalDays}–${listing.maxRentalDays} days`}
            />
            {listing.location ? <Row label="Location" value={listing.location} /> : null}
          </Stack>

          <Stack gap={2} ph={4}>
            <Typography color="white" fontWeight="600">
              Pickup
            </Typography>
            <div className={styles.modeSelector}>
              <div className={classNames(styles.modeOption, styles.modeOptionActive)}>
                <Typography color="white" variant="small" fontWeight="600">
                  Onsite
                </Typography>
              </div>
              <div
                className={classNames(styles.modeOption, styles.modeOptionDisabled)}
                aria-disabled="true"
              >
                <Typography color="lightWhite" variant="small" fontWeight="600">
                  Delivery
                </Typography>
                <span className={styles.soonBadge}>SOON</span>
              </div>
            </div>
            <Typography color="lightWhite" variant="small" className={styles.modeCaption}>
              Delivery is coming soon. For now, contact the dealership manager to arrange it separately.
            </Typography>
          </Stack>

          {listing.description ? (
            <Stack ph={4}>
              <Typography color="white">
                <SafeHtml html={listing.description} />
              </Typography>
            </Stack>
          ) : null}

          {sessionPending ? (
            <Button
              onClick={() => undefined}
              aria-label="Checking your session"
              variant="accent"
              className={styles.bookButton}
              loading
              disabled
            />
          ) : purchaseGate === 'buy' ? (
            <Stack gap={3} className={styles.form}>
              <Typography color="white" fontWeight="600">
                Book this car
              </Typography>
              {openEndedOccupancyNote(listing.occupiedRanges ?? []) ? (
                <Typography color="lightWhite" variant="small">
                  {openEndedOccupancyNote(listing.occupiedRanges ?? [])}
                </Typography>
              ) : null}

              <BookingCalendar
                occupiedRanges={listing.occupiedRanges ?? []}
                startDate={startDate}
                endDate={endDate}
                onSelect={(start, end) => {
                  setError('');
                  setStartDate(start);
                  setEndDate(end);
                }}
              />

              {days > 0 && !validationError && quote != null ? (
                <QuoteRows
                  quote={quote}
                  startDate={startDate}
                  endDate={endDate}
                  listingCurrency={listing.currency}
                  decimals={quoteDecimals(quote.cryptoCurrency, buyerChains, 'rental', listing.currency)}
                />
              ) : null}

              {validationError || error ? (
                <Typography color="white" variant="small" className={styles.error}>
                  {validationError || error}
                </Typography>
              ) : null}

              {escrowOffer.kind === 'offered' ? (
                <Button
                  onClick={handleBook}
                  title="Book"
                  variant="accent"
                  className={styles.bookButton}
                  disabled={!startDate || !endDate || !!validationError}
                  loading={createRentalDeal.isPending}
                />
              ) : (
                // The calendar and the quote stay: dates and price are free to look at. Only the
                // booking itself is withheld, and the sentence says why (app#176).
                <div className={styles.notice} role="status">
                  <Typography color="white" variant="small">
                    {describeEscrowOffer(escrowOffer, 'Booking', listing.currency)}
                  </Typography>
                </div>
              )}
            </Stack>
          ) : purchaseGate === 'open-in-browser' ? (
            <Button
              onClick={() =>
                window.open(`${window.location.origin}/rentals/listing/${listingId}`, '_blank')
              }
              title="Open in browser"
              variant="accent"
              className={styles.bookButton}
            />
          ) : purchaseGate === 'link-wallet' ? (
            <Button
              onClick={handleLinkWallet}
              title="Link a wallet to book"
              variant="accent"
              className={styles.bookButton}
            />
          ) : (
            <Button
              onClick={handleRegister}
              title="Sign up to book"
              variant="accent"
              className={styles.bookButton}
            />
          )}
        </Stack>
      )}
    </div>
  );
};

/**
 * Base units (as quoted by the server) → the amount as shown in the listing currency, scaled by
 * the decimals of the token the rental settles in (`quoteDecimals`).
 */
const formatQuoteAmount = (value: string, decimals: number) =>
  formatBaseUnits(BigInt(value || '0'), decimals);

/**
 * The quote as the renter reads it. Dates and the day count need no token and are shown as soon
 * as the quote is in; the four money lines wait for the decimals of the quote's token
 * (`quoteDecimals`) — while the registry loads, or should it name another token than the quote,
 * they stay off the screen rather than show a number scaled by a guess.
 */
const QuoteRows: FC<{
  quote: ApiRentalBookingQuote;
  startDate: string;
  endDate: string;
  listingCurrency: Currency;
  decimals: number | null;
}> = ({ quote, startDate, endDate, listingCurrency, decimals }) => (
  <Stack gap={1}>
    <Row label="Dates" value={`${formatDay(startDate)} → ${formatDay(endDate)}`} />
    <Row label="Days" value={`${quote.days} × ${formatAmount(quote.dailyPrice)} ${quote.currency}`} />
    {decimals != null ? (
      <>
        <Row label="Rental" value={`${formatQuoteAmount(quote.rentalAmount, decimals)} ${listingCurrency}`} />
        <Row
          label="Deposit (refundable)"
          value={`${formatQuoteAmount(quote.depositAmount, decimals)} ${listingCurrency}`}
        />
        <Row
          label="Platform fee (9%, non-refundable)"
          value={`${formatQuoteAmount(quote.platformFeeAmount, decimals)} ${listingCurrency}`}
        />
        <Row label="Total now" value={`${formatQuoteAmount(quote.totalRequired, decimals)} ${listingCurrency}`} />
      </>
    ) : null}
  </Stack>
);

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack flexDirection="row" justifyContent="space-between" gap={2} flex={1}>
    <Typography color="white">{label}:</Typography>
    <Typography color="lightWhite">{value}</Typography>
  </Stack>
);
